<template>
  <!-- The pager across the top of a warning's details (‹ 1 / 3 ›, PAGER-SPEC.md):
       the arrows step through every warning of the card's group, in route
       order. No wrap-around: the first and last arrows are disabled. -->
  <div class="route-alert-pager" data-test="route-alert-pager">
    <button
      ref="previous"
      type="button"
      class="route-alert-pager__arrow"
      :disabled="!view.canPrevious"
      :aria-label="view.previousLabel"
      data-test="route-alert-pager-previous"
      @click="step('previous')"
    >
      <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
        <path d="M10 3 L5 8 L10 13" />
      </svg>
    </button>
    <!-- Seen as "1 / 3"; read out (again on every step) as "Tree down, 1 of 3 on this route". -->
    <span class="route-alert-pager__count" aria-live="polite" aria-atomic="true">
      <span aria-hidden="true" data-test="route-alert-pager-count">{{ view.countText }}</span>
      <span class="visually-hidden" data-test="route-alert-pager-position">{{ view.positionLabel }}</span>
    </span>
    <button
      ref="next"
      type="button"
      class="route-alert-pager__arrow"
      :disabled="!view.canNext"
      :aria-label="view.nextLabel"
      data-test="route-alert-pager-next"
      @click="step('next')"
    >
      <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
        <path d="M6 3 L11 8 L6 13" />
      </svg>
    </button>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import type { PropType } from 'vue';
import type { PagerView } from '../../../utils/routeAlerts/routeAlertGroups';

export default defineComponent({
  name: 'RouteAlertPagerBar',
  props: {
    view: { type: Object as PropType<PagerView>, required: true },
  },
  emits: ['previous', 'next'],
  data() {
    return { lastStep: null as 'previous' | 'next' | null };
  },
  watch: {
    // The arrow just used is disabled at the end of the group, and a disabled
    // button drops the keyboard focus: hand it to the other arrow.
    'view.canPrevious'(can: boolean) {
      if (!can && this.lastStep === 'previous') this.focusArrow('next');
    },
    'view.canNext'(can: boolean) {
      if (!can && this.lastStep === 'next') this.focusArrow('previous');
    },
  },
  methods: {
    step(direction: 'previous' | 'next') {
      this.lastStep = direction;
      this.$emit(direction);
    },
    focusArrow(direction: 'previous' | 'next') {
      this.$nextTick(() => {
        const button = this.$refs[direction] as HTMLButtonElement | undefined;
        if (button && !button.disabled) button.focus({ preventScroll: true });
      });
    },
  },
});
</script>

<style scoped>
.route-alert-pager {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: space-between;
  padding: 8px 10px;
  background: rgba(255, 255, 255, 0.04);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.route-alert-pager__arrow {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: #2d2f35;
  cursor: pointer;
}

.route-alert-pager__arrow svg path {
  fill: none;
  stroke: #fff;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.route-alert-pager__arrow:hover:not(:disabled) {
  background: #3a3c43;
}

.route-alert-pager__arrow:focus-visible {
  outline: 2px solid #fddf00;
  outline-offset: 2px;
}

.route-alert-pager__arrow:disabled {
  opacity: 0.35;
  cursor: default;
}

.route-alert-pager__count {
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}
</style>
